import React, { useState, useEffect, useMemo } from "react";
import { Container } from "../../components/Container/index";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  message,
} from "antd";
import { SizeBox } from "../../components/SizeBox/index";
import styles from "./styles.module.less";
import { useTitle } from "../../hooks/useTitle";
import { getImgSrc } from "../../utils";
import { useTranslation } from "react-i18next";
import { ImageCrop } from "@/components/ImageCrop";
import { i18n } from "@/locales";
import { RegisterInfoSessionKey } from "@/constants/enum";
import { RegisterInfo } from "@/types/user";
import Axios from "axios";
import { User } from "authing-js-sdk";
import classnames from "classnames";
import { PhoneValidateForm, PhoneValidateFormProps } from "@/components/forms/register-complete/PhoneValidateForm";
import { EmailValidateForm } from "@/components/forms/register-complete/EmailValidateForm";
import { registerUser } from "../Register/registerUser";
import { loginSuccessRedirect } from "@/utils/loginSuccess";
import { useNavigateWithSearch } from "../../hooks/useNavigateWithSearch";
import { IconFont } from "@/components/IconFont";

export interface FieldMetadata {
  key: string;
  options: any;
}

const filterOption = (input: any, option: any) => {
  return option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
    option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
}

const UDF_INPUT_MAP: Record<string, Function> = {
  image: () => <ImageCrop size={70} />,
  number: () => <InputNumber precision={0} style={{ width: "100%" }} />,
  date: () => <DatePicker style={{ width: "100%" }} />,
  datetime: () => <DatePicker style={{ width: "100%" }} />,
  select: (options: any[]) => <Select showSearch options={options} filterOption={filterOption} />,
  dropdown: (options: any[]) => <Select showSearch options={options} filterOption={filterOption} />,
  boolean: () => (
    <Select
      options={[
        { label: i18n.t("common.yes"), value: true as any },
        { label: i18n.t("common.no"), value: false as any },
      ]}
    />
  ),
  string: () => <Input type="text" size="large" />,
  text: () => <Input type="text" size="large" />,
};

const INTERNAL_INPUT_MAP: Record<string, Function> = {
  gender: () => (
    <Select
      options={[
        { label: i18n.t("common.man"), value: "M" },
        { label: i18n.t("common.female"), value: "F" },
      ]}
    ></Select>
  ),
  country: (contryList: any) => (
    <Select options={contryList} showSearch filterOption={filterOption} ></Select>
  ),
  phone: (props: PhoneValidateFormProps) => <PhoneValidateForm required={props.required} />,
  email: (props: PhoneValidateFormProps) => <EmailValidateForm required={props.required} />,
};

export const RegisterCompletePage = () => {
  const { t } = useTranslation();

  useTitle(t("common.registerInfoFill"));
  const navigate = useNavigateWithSearch();
  const [form] = Form.useForm();
  const extendsFields = window.__config__.extendsFields;
  const [contryList, setContryList] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [fieldMetadata, setFieldMetadata] = useState<FieldMetadata[]>([])

  useEffect(() => {
    // 获取contryList
    (async () => {
      const { data: countryList } = await Axios.get("/api/v2/country-list");
      const toMap = i18n.language === "zh-CN" ? countryList?.zh : countryList?.en;
      const toSet: any = [];
      for (const [key, value] of Object.entries(toMap)) {
        toSet.push({
          label: value,
          value: key,
        });
      }
      setContryList(toSet);

      const { data: fieldMetadata } = await Axios.get('/api/v2/udfs/field-metadata-for-completion')
      setFieldMetadata(fieldMetadata);
    })();
  }, []);

  let registerInfo: RegisterInfo | null = null;
  let loginInfo: User | null = null;
  let userToken: string | null = null;
  try {
    registerInfo = JSON.parse(
      sessionStorage.getItem(RegisterInfoSessionKey) || "null"
    );
  } catch (e) {
    // do nothing
  }

  try {
    loginInfo = window.__authing__.tokenProvider.getUser();
    userToken = window.__authing__.tokenProvider.getToken();
  } catch (e) {
    // do nothing
  }

  const onFinish = async (values: any) => {
    const internalFields: any = {};
    const userFields: {
      nameOrId: string;
      value: any;
    }[] = [];
    let phoneToken: string | undefined = undefined;
    let emailToken: string | undefined = undefined;

    let user: User;

    Object.entries(values).forEach(([key, value]) => {
      const [type, nameOrId] = key.split(" ");
      if (type === "internal") {
        if (nameOrId.split(":").length > 1) {
          // 是验证码
          const [codeType, k] = nameOrId.split(":");
          if (codeType === k) {
            if (value !== "" || value) {
              internalFields[codeType] = value;
            }
          } else {
            if (codeType === 'phone') {
              if (value !== "" || value) {
                phoneToken = value as string;
              }
            } else if (codeType === 'email') {
              if (value !== "" || value) {
                emailToken = value as string;
              }
            }
          }
        } else {
          internalFields[nameOrId] = value;
        }
      } else if (type === "user") {
        userFields.push({ nameOrId, value });
      }
    });
    const udfs = userFields.map(({ nameOrId, value }) => ({
      definition: nameOrId,
      value,
    }));
    // 苏州园区 POC 特殊逻辑
    const realnameField = userFields.find(
      (item) => item.nameOrId?.toLowerCase() === "realname"
    );
    if (realnameField) {
      internalFields.familyName = realnameField.value?.[0];
      internalFields.givenName = realnameField.value?.slice?.(1);
    }

    if (registerInfo) {
      /** 从注册页面过来 */
      console.log("注册过来的");
      setLoading(true);

      try {
        user = await registerUser(registerInfo, {
          completeUserInfo: internalFields,
          completeUdfsInfo: udfs.map((udv) => ({
            key: udv.definition,
            value: udv.value,
          })),
          phoneToken,
          emailToken,
          onFail() {
            setLoading(false);
          },
          onSuccess() {
            window.__authing__.tokenProvider.clearUser();
            sessionStorage.removeItem(RegisterInfoSessionKey);
            setTimeout(() => {
              navigate("/login");
              setLoading(false);
            }, 500);
          }
        });
      } catch (error: any) {
        setLoading(false);
      }
    } else {
      /** 从登录页面过来 */
      console.log("登录过来的");
      setLoading(true);
      try {
        user = await window.__authing__.updateProfile(internalFields, {
          phoneToken,
          emailToken,
        });
        await Axios.post(
          `/api/v2/udfs/values`,
          {
            udfs,
          },
          {
            headers: {
              authorization: userToken,
            },
          }
        );
        message.success(t("common.operateSuccess"));

        setTimeout(() => {
          loginSuccessRedirect(user);
        }, 500);
      } catch (e) {
        console.log("出错了")
        setLoading(false);
        return;
      } finally {
      }
    }
  };

  const items = extendsFields.filter(field => {
    const { name } = field;
    // 从注册页面来的，此时数据还没落库，存在 sessionStorage 里面。如果是邮箱注册，隐藏邮箱补全；如果是手机号注册，隐藏手机号补全
    if (name === 'email' && registerInfo?.email) {
      return false;
    }
    if (name === 'phone' && registerInfo?.phone) {
      return false;
    }
    console.log(loginInfo);
    // 登录之后要求补全信息
    if (loginInfo) {
      // username 默认就是手机号或者邮箱
      if (name === "username") {
        if (loginInfo[name] === loginInfo['phone'] || loginInfo[name] === loginInfo['email']) {
          return true;
        }
      }

      // gender 默认是 U
      if (name === "gender" && loginInfo[name] === "U") {
        return true;
      }
      // 如果基础字段里面已经有了，不再要求补全
      if (loginInfo[name as keyof User] !== undefined && loginInfo[name as keyof User] !== null) {
        return false
      }
      // 如果扩展信息又了，不再要求补全
      const customData = loginInfo.customData;
      if (customData && customData[name] !== undefined && customData[name] !== null) {
        return false
      }
    }

    return true;
  }).map((def) => {
    const key = `${def.type} ${def.name}`;
    const label = i18n.language === "zh-CN" ? def.label || def.name : def.name;
    const required = def.required || false;
    const rules = def.validateRules;
    const generateRules = () => {
      const l = [];
      if (required) {
        l.push({
          required: true,
          message: `${label} ${t("login.noEmpty")}`,
        });
      }
      rules.forEach((rule) => {
        switch (rule.type) {
          case "isNumber":
            l.push({
              type: "number",
              required,
              message: rule.error || t("login.mustBeNumber"),
            });
            break;
          case "regExp":
            l.push({
              type: "regexp",
              required,
              message: rule.content,
            });
            break;
          default:
            break;
        }
      });
      return l;
    };
    const inputElement = () => {
      if (def.type === "internal" && Object.keys(INTERNAL_INPUT_MAP).includes(def.name)) {
        if (def.name === "country") {
          return INTERNAL_INPUT_MAP[def.name](contryList);
        } else {
          return INTERNAL_INPUT_MAP[def.name]({
            required
          });
        }
      } else {
        if (Object.keys(UDF_INPUT_MAP).includes(def.inputType)) {
          if (def.inputType === "select") {
            const options = fieldMetadata.find(field => field.key === def.name)?.options || [];
            return UDF_INPUT_MAP[def.inputType](options);
          }
          return UDF_INPUT_MAP[def.inputType]();
        }
        return <Input type="text" size="large" />;
      }
    };

    const outPut = () => {
      if (def.type === "internal" && ["phone", "email"].includes(def.name)) {
        return inputElement();
      } else {
        return (
          <Form.Item
            rules={generateRules()}
            key={key}
            name={key}
            label={label}
            style={{ marginBottom: 8 }}
          >
            {inputElement()}
          </Form.Item>
        );
      }
    };
    return outPut();
  });

  const onSkip = () => {
    if (registerInfo) {
      window.__authing__.tokenProvider.clearUser();
      sessionStorage.removeItem(RegisterInfoSessionKey);
      setTimeout(() => {
        navigate("/login");
        setLoading(false);
      }, 500);
    }
    else {
      setTimeout(() => {
        loginInfo && loginSuccessRedirect(loginInfo);
      }, 500);
    }
  }

  const showSkip = Boolean(window.__config__?.skipComplateFileds);

  return (
    <Container minHeight="auto" style={{
      position: 'relative'
    }}>
      <h1
        className={classnames(styles.title, "authing-complete-user-info-title")}
      >
        {t("common.perfectUserInfo")}
      </h1>
      {showSkip &&
        <span
          className={styles.skip}
          onClick={onSkip}
        >
          <IconFont type="authing-a-share-forward-line1" />
          <span>{t('common.skip')}</span>
        </span>
      }


      <div
        className={classnames(
          styles.subTitle,
          "authing-complete-user-info-sub-title"
        )}
      >
        <p>
          {t("common.welcomeJoinAndPerfect", {
            name: window.__config__.name,
          })}
        </p>
        <img
          className={classnames(styles.tipImg, "authing-complete-user-tip-img")}
          src={getImgSrc("/20200904122043.svg")}
          alt=""
        />
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        {items}

        <SizeBox height={20} />
        <Form.Item>
          <Button
            loading={loading}
            block
            size="large"
            type="primary"
            htmlType="submit"
            className="authing-complete-user-info-btn"
          >
            {t("common.submit")}
          </Button>
        </Form.Item>
      </Form>
    </Container>
  );
};
